/**
 * Lossless V2 → V3 migration, and the reverse lowering for the degenerate
 * case.
 *
 * "Lossless" here is a hard requirement, not an aspiration: the upgrade wraps
 * a V2 root into one stage and changes nothing else. No numeric is rounded, no
 * effect node is reordered or deduplicated, no source is re-resolved, no
 * default is filled in. A skill that round-trips V2 → V3 → V2 must come back
 * byte-identical, which is what lets the loader keep serving existing skills
 * through the existing compiler while V3 is still being built out.
 */
import {
  CANONICAL_SKILL_V3_SCHEMA_VERSION,
  PRIMARY_STAGE_ID,
  SINGLE_ATTACK_PATTERN_V1,
  isDegenerateCompositeSkill,
  type AttackStageV1,
  type CanonicalSkillV3Input,
} from './composite-types.js';
import { CANONICAL_SKILL_V2_SCHEMA_VERSION, type CanonicalSkillV2Input } from './types.js';

/**
 * Wrap a V2 skill's single root application/effects pair into one
 * `AT_RELEASE` stage carrying the degenerate single-member, single-volley
 * pattern.
 *
 * The stage's `application` and `effects` are the *same references* the V2
 * input holds — not copies — so no structural drift can be introduced here,
 * and the input itself is never mutated.
 */
export function upgradeCanonicalV2ToV3(input: CanonicalSkillV2Input): CanonicalSkillV3Input {
  const { schemaVersion: _schemaVersion, application, effects, ...rest } = input;

  const primary: AttackStageV1 = {
    stageId: PRIMARY_STAGE_ID,
    timing: { kind: 'AT_RELEASE' },
    application,
    pattern: SINGLE_ATTACK_PATTERN_V1,
    effects,
  };

  return { ...rest, schemaVersion: CANONICAL_SKILL_V3_SCHEMA_VERSION, stages: [primary] };
}

/**
 * Lower a degenerate composite skill — one stage, one member, one volley,
 * released with the cast — back to its exact V2 form.
 *
 * Returns `undefined` for anything genuinely composite. That is the honest
 * answer and the important one: a multi-stage or multi-member skill has no
 * V2 representation, and inventing a reduced one would be precisely the
 * silent truncation the contract forbids. Callers turn `undefined` into a
 * typed `DEFERRED`, never into a smaller skill.
 */
export function lowerDegenerateV3ToV2(input: CanonicalSkillV3Input): CanonicalSkillV2Input | undefined {
  if (!isDegenerateCompositeSkill(input)) return undefined;

  const { schemaVersion: _schemaVersion, stages, ...rest } = input;
  const only = stages[0]!;

  return {
    ...rest,
    schemaVersion: CANONICAL_SKILL_V2_SCHEMA_VERSION,
    application: only.application,
    effects: only.effects,
  } as CanonicalSkillV2Input;
}

export { validateCompositeSkill, type CompositeValidationResult } from './composite-validate.js';
